import { ref } from 'vue'
import { defineStore } from 'pinia'
import { createDiscreteApi } from 'naive-ui'
import { api, useDelete } from '@/plugins/api'
import router from '@/router'
import type { Integrator, Location, MappingOutput, GlobalState, Status, Company, Center } from '@/infrastructure/types'
import { DataGapStatus } from '@/infrastructure/enums'

const { notification, dialog } = createDiscreteApi(['notification', 'dialog'])

export const useDefaultStore = defineStore('default', () => {
  // State
  const isLoading = ref(false)
  const isInitializing = ref(false)
  const isInitialized = ref(false)
  const pageTitle = ref('')
  const globalState = ref<GlobalState>({} as GlobalState)
  const statuses = ref<Status[]>([])
  const integrators = ref<Integrator[]>([])
  const locations = ref<Location[]>([])
  const outputs = ref<MappingOutput[]>([])
  
  const dataSources = ref([
    { id: 1, dataSourceType: 'Unknown' },
    { id: 2, dataSourceType: 'Manual' },
    { id: 3, dataSourceType: 'Push' },
    { id: 4, dataSourceType: 'Pull' },
    { id: 5, dataSourceType: 'FTPS' },
  ])
  
  const actionPhases = ref([
    { label: 'Startup', value: 0 },
    { label: 'Integration', value: 1 },
    { label: 'Verification', value: 2 },
    { label: 'Consolidation', value: 3 },
    { label: 'Completed', value: 4 }
  ])

  // Actions
  async function checkIsServiceMode(): Promise<void> {
    try {
      const { data: isServiceMode } = await api.get('service-mode/is-enabled')
      const path = router.currentRoute.value.path

      if (isServiceMode) {
        localStorage.setItem('serviceMode', 'true')
      } else {
        localStorage.removeItem('serviceMode')
      }

      if (isServiceMode && path !== '/service-mode') {
        router.push('/service-mode')
      }
      else if (!isServiceMode && path === '/service-mode') {
        router.push('/')
      }
    } catch (error) {
      console.error('Unable to determine service mode')
    }
  }

  async function getOpenDataGapCount(): Promise<void> {
    try {
      const { data } = await api.get('data-gaps/active-count')

      if (typeof data === 'number') {
        globalState.value.openDataGapCount = data
      }
    } catch (error) {
      console.error('Unable to get open data gaps', error)
    }
  }

  async function loadStatuses(): Promise<void> {
    try {
      const { data } = await api.get('statuses')
      statuses.value = data
    } catch (error) {
      notification.error({
        title: 'Could not get statuses',
        description: 'Some features might not work as expected',
        content: 'It might help to reload the page.',
        duration: 5000
      })
    }
  }

  async function loadIntegrators(): Promise<void> {
    try {
      const { data } = await api.get('integrators')
      integrators.value = data
    } catch (error) {
      notification.error({
        title: 'Could not get integrators',
        description: 'Some features might not work as expected',
        content: 'It might help to reload the page.',
        duration: 5000
      })
    }
  }

  async function loadLocations(): Promise<void> {
    try {
      const { data } = await api.get('locations')
      locations.value = data
      return data
    } catch (error) {
      notification.error({
        title: 'Could not get locations',
        description: 'Some features might not work as expected',
        content: 'It might help to reload the page.',
        duration: 5000
      })
    }
  }

  async function loadCompanies(): Promise<Company[]> {
    try {
      const { data } = await api.get('companies')
      return data as Company[]
    }
    catch (error) {
      notification.error({
        title: 'Could not get companies',
        description: 'Something went wrong while getting companies',
        content: 'Reloading the page might help.',
        duration: 5000
      })

      return []
    }
  }

  async function loadCenters(): Promise<Center[]> {
    try {
      const { data } = await api.get('centers')
      return data as Center[]
    }
    catch (error) {
      notification.error({
        title: 'Could not get center',
        description: 'Something went wrong while getting center',
        content: 'Reloading the page might help.',
        duration: 5000
      })

      return []
    }
  }

  async function loadOutputs(): Promise<void> {
    try {
      const { data } = await api.get('mappings/outputs')
      outputs.value = data
    } catch (error) {
      notification.error({
        title: 'Could not get outputs',
        description: 'Some features might not work as expected',
        content: 'It might help to reload the page.',
        duration: 5000
      })
    }
  }

  async function unlinkShopContract(shopContractId: number): Promise<void> {
    return new Promise((resolve, reject) => {
      dialog.warning({
        title: 'Are you sure?',
        content: `This will remove the link between this contract and the shop.`,
        positiveText: 'Yes, unlink',
        negativeText: 'No, take me back',
        onPositiveClick: async () => {
          try {
            await useDelete('shop-contracts/' + shopContractId, { showFeedback: false })
            notification.success({ content: 'Unlink successful', duration: 5000 })
            resolve()
          } catch (error) {
            // @ts-expect-error
            notification.error({ content: error.response.data.detail, duration: 5000 })
            reject()
          }
        }
      })
    })
  }

  return {
    // State
    isLoading,
    isInitialized,
    isInitializing,
    pageTitle,
    globalState,
    statuses,
    integrators,
    locations,
    outputs,
    dataSources,
    actionPhases,
    
    // Actions
    checkIsServiceMode,
    getOpenDataGapCount,
    loadStatuses,
    loadIntegrators,
    loadLocations,
    loadCompanies,
    loadCenters,
    loadOutputs,
    unlinkShopContract
  }
})